import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import { buildCharacter, type CharacterRig } from '@/meshkit/character/build';
import { CharacterAnimator } from '@/meshkit/character/animator';
import { LUCA } from '@/meshkit/character/spec';
import type { CollisionWorld } from '@/world/collision';
import { bodies, moveActor } from '@/world/bodies';
import type { Input } from '@/core/Input';
import type { Faction } from '@/gameplay/GameState';
import { PISTOL, Weapon, type Combat, type CombatTarget } from '@/gameplay/Combat';

/**
 * Luca Vitale — the controllable player.
 *
 * Movement is velocity-based with acceleration and friction: no direct per-frame
 * teleportation, so direction changes have weight. While aiming, Luca turns toward the mouse
 * instead of his movement direction, allowing sidestepping while firing for mission 1.
 */

export const PLAYER = {
  walkSpeed: 3.2,
  runSpeed: 5.1,
  acceleration: 26,
  friction: 14,
  /** Collision circle in the XZ plane. */
  radius: 0.34,
  /** Radians per second used to rotate toward the target direction. */
  turnRate: 14,
  maxHealth: 100,
} as const;

export class Player implements CombatTarget {
  readonly rig: CharacterRig;
  readonly object: THREE.Object3D;

  readonly position = new THREE.Vector2();
  readonly velocity = new THREE.Vector2();

  health: number = PLAYER.maxHealth;
  aiming = false;

  /** Luca's own weapon, with its own magazine. */
  readonly weapon = new Weapon(PISTOL);

  /** For `CombatTarget`: where bullets can hit him, and which side he is on. */
  readonly radius = PLAYER.radius;
  readonly faction: Faction = 'vitale';

  readonly #animator: CharacterAnimator;
  readonly #desired = new THREE.Vector2();
  readonly #step = new THREE.Vector2();
  readonly #aim = new THREE.Vector2();
  #facing = 0;
  #stepDistance = 0;

  /**
   * @param rig A preloaded rig, for example the skinned HD model from `loadCharacterRig()`.
   *            Without one, Luca is built procedurally so tests and tools can run without a GLB.
   */
  constructor(spawn: THREE.Vector2, rig: CharacterRig = buildCharacter(LUCA)) {
    this.rig = rig;
    this.object = this.rig.root;
    this.#animator = new CharacterAnimator(this.rig);
    this.position.copy(spawn);
    this.#sync();
  }

  get speed(): number {
    return this.velocity.length();
  }

  get alive(): boolean {
    return this.health > 0;
  }

  /** World position at chest height, used as the origin for bullets and sight lines. */
  chestPoint(out = new THREE.Vector3()): THREE.Vector3 {
    return out.set(this.position.x, 1.2, this.position.y);
  }

  /**
   * @param combat Without this, Luca still moves but does not shoot. Useful in tests and on
   *               the review page, where no combat system is running.
   */
  update(
    dt: number,
    input: Input,
    world: CollisionWorld,
    cameraYaw: number,
    combat?: Combat,
  ): void {
    if (!this.alive) return;

    input.movement(this.#desired, cameraYaw);
    this.aiming = input.firing;

    const maxSpeed = input.isDown('shift') && !this.aiming ? PLAYER.runSpeed : PLAYER.walkSpeed;

    if (this.#desired.lengthSq() > 0) {
      this.velocity.addScaledVector(this.#desired, PLAYER.acceleration * dt);
      if (this.velocity.length() > maxSpeed) this.velocity.setLength(maxSpeed);
    } else {
      // Friction: exponential damping, then snap tiny residual speeds to zero.
      const damping = Math.max(0, 1 - PLAYER.friction * dt);
      this.velocity.multiplyScalar(damping);
      if (this.velocity.lengthSq() < 0.0004) this.velocity.set(0, 0);
    }

    this.#step.copy(this.velocity).multiplyScalar(dt);
    moveActor(this, this.#step, world, bodies);

    this.#updateFacing(dt, input);
    this.#shoot(dt, input, combat);
    this.#emitFootsteps();
    this.#animator.update(dt, this.speed, this.aiming);
    this.#sync();
  }

  /**
   * The mouse button does two things: holding aims, and each click fires one bullet. The
   * pistol is semi-automatic, so holding does not empty the magazine. R reloads, and clicking
   * on empty starts a reload too.
   */
  #shoot(dt: number, input: Input, combat: Combat | undefined): void {
    this.weapon.update(dt);
    if (!combat) return;

    if (input.wasPressed('r')) {
      const wasReloading = this.weapon.reloading;
      this.weapon.reload();
      if (!wasReloading && this.weapon.reloading) {
        bus.emit('weapon:reload', {
          x: this.position.x,
          z: this.position.y,
          weapon: this.weapon.spec.name,
          faction: this.faction,
        });
      }
    }

    const wants = this.weapon.spec.automatic ? input.firing : input.firePressed;
    if (!wants) return;

    if (this.weapon.empty) {
      const wasReloading = this.weapon.reloading;
      this.weapon.reload();
      if (!wasReloading && this.weapon.reloading) {
        bus.emit('weapon:reload', {
          x: this.position.x,
          z: this.position.y,
          weapon: this.weapon.spec.name,
          faction: this.faction,
        });
      }
      return;
    }
    if (!this.weapon.pullTrigger()) return;

    this.#aim.set(input.aimPoint.x, input.aimPoint.z);
    combat.fire(this.position, this.#aim, this.weapon.spec, this);
  }

  damage(amount: number): void {
    if (!this.alive) return;
    this.health = Math.max(0, this.health - amount);
    bus.emit('player:damaged', { amount, healthLeft: this.health });
    if (this.health === 0) bus.emit('player:died', {});
  }

  resetTo(checkpoint: THREE.Vector2): void {
    this.position.copy(checkpoint);
    this.velocity.set(0, 0);
    this.health = PLAYER.maxHealth;
    this.aiming = false;
    this.weapon.reset();
    this.#facing = 0;
    this.#stepDistance = 0;
    this.object.visible = true;
    this.#sync();
  }

  #emitFootsteps(): void {
    if (this.speed < 0.35) {
      this.#stepDistance = 0;
      return;
    }

    this.#stepDistance += this.#step.length();
    const stride = this.speed > PLAYER.walkSpeed + 0.2 ? 0.72 : 0.86;
    if (this.#stepDistance < stride) return;

    this.#stepDistance %= stride;
    bus.emit('player:step', {
      x: this.position.x,
      z: this.position.y,
      speed: this.speed,
    });
  }

  /**
   * Direction Luca faces. While aiming, he faces the mouse; otherwise he follows movement.
   * Rotation is damped so the character cannot flip in a single frame.
   */
  #updateFacing(dt: number, input: Input): void {
    let target = this.#facing;

    if (this.aiming) {
      target = Math.atan2(
        input.aimPoint.x - this.position.x,
        input.aimPoint.z - this.position.y,
      );
    } else if (this.velocity.lengthSq() > 0.02) {
      target = Math.atan2(this.velocity.x, this.velocity.y);
    }

    const delta = wrapAngle(target - this.#facing);
    this.#facing += delta * Math.min(1, PLAYER.turnRate * dt);
  }

  #sync(): void {
    this.object.position.set(this.position.x, 0, this.position.y);
    this.object.rotation.y = this.#facing;
  }
}

/** Wrap an angle to (−π, π], so rotation always takes the short path. */
function wrapAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}
